import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Users, FileText, Loader2, Play } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

export default function BulkEmail() {
  const { activeCompanyId } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    account_id: "",
    subject: "",
    body_html: "",
    recipients_raw: "",
    hourly_rate_limit: 100,
    template_id: "",
  });

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [a, t, c] = await Promise.all([
      api.get("/email-accounts", { params }),
      api.get("/templates", { params }),
      api.get("/contacts", { params }),
    ]);
    setAccounts(a.data); setTemplates(t.data); setContacts(c.data);
    if (!form.account_id && a.data.length) setForm((f) => ({ ...f, account_id: a.data[0].id }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  // Poll for job progress
  useEffect(() => {
    if (!job || job.status === "done") return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/bulk/jobs/${job.id}`);
        setJob(data);
        if (data.status === "done") clearInterval(interval);
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(interval);
  }, [job]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({
      ...f,
      template_id: id,
      subject: t.subject || f.subject,
      body_html: t.body_html || t.body_text?.replace(/\n/g, "<br>") || f.body_html,
    }));
    toast.success("Template applied");
  };

  const importAllContacts = () => {
    const list = contacts.map((c) => `${c.email},${c.full_name || ""},${c.customer_company || ""}`).join("\n");
    setForm((f) => ({ ...f, recipients_raw: list }));
    toast.success(`Imported ${contacts.length} contacts`);
  };

  const parseRecipients = () => {
    return form.recipients_raw
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [email, name, company] = line.split(",").map((x) => (x || "").trim());
        return { email, variables: { name, company, email } };
      })
      .filter((r) => r.email && r.email.includes("@"));
  };

  const submit = async () => {
    const recipients = parseRecipients();
    if (!form.account_id) return toast.error("Choose a sending account");
    if (recipients.length === 0) return toast.error("Add at least one valid recipient");
    if (!form.subject) return toast.error("Add a subject");
    setBusy(true);
    try {
      const bodyText = form.body_html.replace(/<[^>]+>/g, " ");
      const { data } = await api.post("/bulk/send", {
        account_id: form.account_id,
        subject: form.subject,
        body_html: form.body_html,
        body_text: bodyText,
        hourly_rate_limit: form.hourly_rate_limit,
        recipients,
      });
      setJob(data);
      toast.success(`Job started — ${data.total} recipients`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start job");
    }
    setBusy(false);
  };

  const recipients = parseRecipients();

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="bulk-page">
      <PageHeader
        title="Bulk email"
        subtitle="Send templated campaigns from any authorized SMTP profile — with variable substitution and rate throttling."
        eyebrow="Outreach"
      />
      <div className="p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="card-3d p-6 space-y-4">
          <F label="From account (SMTP profile)">
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger className="rounded-xl" data-testid="bulk-account-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email_address}</SelectItem>)}</SelectContent>
            </Select>
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Template">
              <Select value={form.template_id} onValueChange={applyTemplate}>
                <SelectTrigger className="rounded-xl" data-testid="bulk-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Rate limit (per hour)">
              <Input type="number" value={form.hourly_rate_limit} onChange={(e) => setForm({ ...form, hourly_rate_limit: Number(e.target.value) })} className="rounded-xl" data-testid="bulk-rate-input" />
            </F>
          </div>

          <F label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-xl" data-testid="bulk-subject-input" placeholder="Supports {{name}}, {{company}}, {{email}}" /></F>

          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Body — variables: <span className="text-foreground">{"{{name}} {{company}} {{email}}"}</span></Label>
            <RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} placeholder="Write your message with {{name}} and {{company}} tokens…" />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-3d p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recipients</div>
                <div className="font-display text-2xl font-semibold tracking-tight mt-0.5">{recipients.length}</div>
              </div>
              <Button variant="outline" size="sm" className="rounded-lg" onClick={importAllContacts} data-testid="import-contacts-button">
                <Users className="h-3.5 w-3.5 mr-1.5" /> Import contacts
              </Button>
            </div>
            <Textarea
              rows={8}
              value={form.recipients_raw}
              onChange={(e) => setForm({ ...form, recipients_raw: e.target.value })}
              placeholder={"email@example.com,Full Name,Company\njane@example.com,Jane Doe,Acme"}
              className="mt-3 rounded-xl font-mono text-xs"
              data-testid="bulk-recipients-input"
            />
            <div className="text-[11px] text-muted-foreground mt-2 font-mono">One per line · CSV: email, name, company</div>
          </div>

          <div className="card-3d p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <Play className="h-3 w-3 text-emerald-brand" /> Launch
            </div>
            <Button className="w-full rounded-xl h-11 shadow-lg shadow-primary/20" onClick={submit} disabled={busy} data-testid="bulk-send-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Start bulk send</>}
            </Button>

            {job && (
              <div className="mt-4 space-y-2" data-testid="bulk-job-progress">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">Job {job.id.slice(0, 8)}</span>
                  <span className={`font-mono text-[10px] uppercase ${job.status === "done" ? "text-emerald-brand" : "text-sun"}`}>{job.status}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-emerald-brand to-sun"
                    initial={{ width: 0 }}
                    animate={{ width: `${((job.sent + job.failed) / Math.max(job.total, 1)) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-emerald-brand">✓ {job.sent} sent</span>
                  <span className="text-crimson">✕ {job.failed} failed</span>
                  <span className="text-muted-foreground">of {job.total}</span>
                </div>
                {job.errors?.length > 0 && (
                  <div className="text-[10px] font-mono text-muted-foreground max-h-24 overflow-y-auto">
                    {job.errors.slice(-3).map((e, i) => <div key={i}>· {e.to}: {e.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
